import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import {
  useCheckRegionAvailability,
  useCheckSubdomainAvailability,
  useUpdateRegion,
} from 'api/regions/hooks';
import { Form, Spinner } from 'components';
import { ErrorFeedback } from 'components/Form/styles';
import { currencies } from 'constants/currenciesList';
import * as Roles from 'constants/roles';
import { useFormikContext } from 'formik';
import { useAuthStore } from 'stores/authStore';
import { mapInitialValues } from 'utils/regionFormUtils';
import validationSchemas from './schemas';

const RegionForm = ({ type = 'update', initialValues, FormGroup }) => {
  const [debouncedRegionName, setDebouncedRegionName] = useState(null);
  const [debouncedSubdomain, setDebouncedSubdomain] = useState(null);

  const { t } = useTranslation(['pages', 'app']);
  const validationSchema =
    type === 'create' ? validationSchemas.region : validationSchemas.regionUpdate;
  const role = useAuthStore((state) => state.role);
  const brand = useAuthStore((state) => state.user?.admin_details?.brand);

  const { mutateAsync: updateRegion, isPending: isUpdating } = useUpdateRegion();

  const isSuperAdmin = role === Roles.ADMIN;
  const isSubmitting = isUpdating;

  const { data: regionAvailabilityData, isFetching: isRegionLoading } =
    useCheckRegionAvailability(debouncedRegionName);

  const { data: subdomainAvailabilityData, isFetching: isDomainLoading } =
    useCheckSubdomainAvailability(debouncedSubdomain);

  const isRegionAvailable = regionAvailabilityData?.available ?? true;
  const isDomainAvailable = subdomainAvailabilityData?.available ?? true;
  const regionAvailabilityMessage =
    regionAvailabilityData?.message || t('pages:regions.nameNotAvailable');
  const domainAvailabilityMessage =
    subdomainAvailabilityData?.message || t('pages:regions.subdomainNotAvailable');

  const domainTaken = 'fe-x-circle text-danger';
  const domainAvailable = 'fe-check-circle text-success';

  const debouncedSetRegionName = useMemo(
    () =>
      debounce((name) => {
        setDebouncedRegionName(name);
      }, 500),
    [],
  );

  const debouncedSetSubdomain = useMemo(
    () =>
      debounce((subdomain) => {
        setDebouncedSubdomain(subdomain);
      }, 500),
    [],
  );

  const RegionAvailabiltyCheck = ({ hasRegionChanged }) => {
    const { values, validateField } = useFormikContext();

    useEffect(() => {
      if (hasRegionChanged && values?.name?.length && isSuperAdmin) {
        debouncedSetRegionName(values.name);
      } else if (!hasRegionChanged) {
        debouncedSetRegionName(null);
      }

      return () => {
        debouncedSetRegionName.cancel();
      };
    }, [values.name, hasRegionChanged]);

    useEffect(() => {
      validateField('name');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRegionAvailable, validateField]);

    return null;
  };

  const DomainAvailabilityCheck = ({ hasDomainChanged }) => {
    const { values, validateField } = useFormikContext();

    useEffect(() => {
      if (hasDomainChanged && values?.subdomain?.length && isSuperAdmin) {
        debouncedSetSubdomain(values.subdomain);
      } else if (!hasDomainChanged) {
        debouncedSetSubdomain(null);
      }

      return () => {
        debouncedSetSubdomain.cancel();
      };
    }, [values.subdomain, hasDomainChanged]);

    useEffect(() => {
      validateField('subdomain');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDomainAvailable, validateField]);

    return null;
  };

  const mappedInitialValues = useMemo(
    () => mapInitialValues(initialValues, brand?.uid),
    [initialValues, brand],
  );

  return (
    <Form
      initialValues={mappedInitialValues}
      onSubmit={async ({ uid, name, subdomain, base_currency, can_retailers_edit_tracking_id }) => {
        if (type === 'update') {
          await updateRegion({
            uid,
            name,
            subdomain,
            base_currency,
            can_retailers_edit_tracking_id,
          });
        }
      }}
      validationSchema={validationSchema}
      renderDefaultButtons={false}
      pristine
    >
      {({ values, initialValues: formikInitialValues, errors, touched }) => {
        const hasRegionChanged = values?.name !== formikInitialValues?.name;
        const hasDomainChanged = values?.subdomain !== formikInitialValues?.subdomain;

        return (
          <>
            <FormGroup required label={t('pages:regions.name')}>
              <Form.Input
                type="text"
                name="name"
                validate={() =>
                  !hasRegionChanged || isRegionAvailable ? undefined : regionAvailabilityMessage
                }
                showErrorFeedback={false}
                prepend={
                  isSubmitting || isRegionLoading ? (
                    <Spinner style={{ width: '17px', height: '17px' }} />
                  ) : (!hasRegionChanged || isRegionAvailable) && !errors?.name && isSuperAdmin ? (
                    <i className={`fe ${domainAvailable}`} />
                  ) : (
                    <i className={`fe ${domainTaken}`} />
                  )
                }
                disabled={!isSuperAdmin}
              />
              <RegionAvailabiltyCheck hasRegionChanged={hasRegionChanged} />
              {((touched.name && errors.name) || errors.name === regionAvailabilityMessage) && (
                <ErrorFeedback>{errors.name}</ErrorFeedback>
              )}
            </FormGroup>
            <FormGroup required label={t('pages:regions.subdomain')}>
              <Form.Input
                type="text"
                name="subdomain"
                validate={() =>
                  !hasDomainChanged || isDomainAvailable ? undefined : domainAvailabilityMessage
                }
                showErrorFeedback={false}
                placeholder="my-custom-subdomain"
                append=".ramblas.io"
                prepend={
                  isSubmitting || isDomainLoading ? (
                    <Spinner style={{ width: '17px', height: '17px' }} />
                  ) : (!hasDomainChanged || isDomainAvailable) &&
                    !errors?.subdomain &&
                    isSuperAdmin ? (
                    <i className={`fe ${domainAvailable}`} />
                  ) : (
                    <i className={`fe ${domainTaken}`} />
                  )
                }
                disabled={!isSuperAdmin}
              />

              <DomainAvailabilityCheck hasDomainChanged={hasDomainChanged} />
              {((touched.subdomain && errors.subdomain) ||
                errors.subdomain === domainAvailabilityMessage) && (
                <ErrorFeedback>{errors.subdomain}</ErrorFeedback>
              )}
            </FormGroup>
            <FormGroup
              required
              label={t('pages:regions.currency')}
              tooltipText={t('pages:regions.currencyTooltip')}
            >
              <Form.MultiSelect
                options={currencies.map((currency) => ({
                  value: currency.code,
                  label: currency.description,
                }))}
                name="base_currency"
                isMulti={false}
                disabled={!isSuperAdmin || type === 'update'}
              />
            </FormGroup>
            <FormGroup label={t('pages:regions.canRetailersEditTrackingId')}>
              <Form.Select
                name="can_retailers_edit_tracking_id"
                options={[
                  { value: true, label: t('app:yes') },
                  { value: false, label: t('app:no') },
                ]}
              />
            </FormGroup>
            <Form.Group style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Form.Submit
                variant="primary"
                type="submit"
                isLoading={isUpdating}
                ignoreClickedState
                pristine
              >
                {t(`app:save`).toUpperCase()}
              </Form.Submit>
            </Form.Group>
          </>
        );
      }}
    </Form>
  );
};

RegionForm.propTypes = {
  type: PropTypes.oneOf(['create', 'update']),
  initialValues: PropTypes.object,
  FormGroup: PropTypes.func,
};

export default RegionForm;
